import { Box, Typography, useTheme } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';

import {
  CreditsDurationInfo,
  EpisodeCountWithCompletions,
  ProgressBar,
} from '@/components';
import { Routes } from '@/constants';
import { Link } from '@/containers';
import { CheckmarkIcon } from '@/icons';
import { pinnedLpStar } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import { LearningPathWithDuration, Referrer } from '@/types';
import { createRouteWithParams } from '@/utils';

import {
  LearningPathCardDimensions,
  LearningPathCardVariants,
} from './constants';

export const LearningPathHorizontalCard = ({
  learningPath,
  referrer,
}: {
  learningPath: LearningPathWithDuration;
  referrer: Referrer;
}) => {
  const { containerQueries } = useTheme();
  const { pathname, search } = useLocation();
  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.LEARNING_PATH_CARD,
    variants: {
      ...referrer.variants,
      lpCardVariant: LearningPathCardVariants.HORIZONTAL,
    },
  };

  const { ref } = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange: inView => {
      if (inView) {
        AnalyticsService.learningPathImpression({
          learningPathId: learningPath.groupId,
          location,
          referrer,
        });
      }
    },
  });

  const episodeCount = learningPath.episodes.length || 0;
  const progressFraction = learningPath.duration
    ? Math.min(learningPath.durationPlayed / learningPath.duration, 1)
    : 0;

  const MEDIUM_BREAKPOINT = 400;
  const LARGE_BREAKPOINT = 580;
  const {
    height: heightSm,
    heightLarge: heightLg,
    coverImageWidth,
    coverImageHeight,
    coverImageWidthLarge,
    coverImageHeightLarge,
  } = LearningPathCardDimensions[LearningPathCardVariants.HORIZONTAL];
  const HEIGHT = {
    sm: heightSm,
    lg: heightLg,
  };

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        backgroundColor: 'white',
        border: 1,
        borderColor: 'secondary.medium',
        borderRadius: 4,
        height: HEIGHT.sm,
        '&:hover': {
          borderColor: 'secondary.active',
          backgroundColor: 'secondary.light',
        },
        transition: 'border .1s ease, background-color .1s ease',
        '&:hover .cover-image': { transform: 'scale(1.1)' },
        [containerQueries.up(LARGE_BREAKPOINT)]: {
          height: HEIGHT.lg,
        },
      }}>
      <Link
        variant="plain"
        to={createRouteWithParams(Routes.LEARNING_PATH, {
          learningPathId: learningPath.groupId,
        })}
        onClick={() => {
          AnalyticsService.learningPathCardClicked({
            learningPathId: learningPath.groupId,
            location,
            referrer,
            episodesCompleted: learningPath.completions,
            isCompleted: learningPath.isCompleted,
            topic: learningPath.topic?.name ?? '',
            isRecommended: learningPath.isPinnedByOrg,
          });
        }}
        state={{ referrer, from: { pathname, search } }}>
        <Box
          sx={{
            position: 'relative',
            height: HEIGHT.sm,
            display: 'flex',
            flex: 1,
            gap: 1.5,
            p: 2,
            flexDirection: 'row',
            alignItems: 'stretch',
            textAlign: 'left',
            [containerQueries.up(LARGE_BREAKPOINT)]: {
              height: HEIGHT.lg,
              gap: 5,
              py: 4,
              px: 6,
            },
          }}>
          {learningPath.isPinnedByOrg && (
            <Box
              component="img"
              src={pinnedLpStar}
              alt="pinned lp star"
              sx={{
                width: 24,
                height: 24,
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            />
          )}
          <Box
            sx={{
              position: 'relative',
              width: coverImageWidth,
              height: coverImageHeight,
              overflow: 'hidden',
              borderRadius: '50%',
              flexShrink: 0,
              alignSelf: 'center',
              my: 'auto',
              [containerQueries.up(LARGE_BREAKPOINT)]: {
                width: coverImageWidthLarge,
                height: coverImageHeightLarge,
              },
            }}>
            <Box
              sx={[
                {
                  width: '100%',
                  height: '100%',
                  transition: 'transform .25s ease-in-out',
                },
                learningPath.isCompleted && { filter: 'grayscale(1)' },
              ]}
              className="cover-image"
              component="img"
              src={learningPath.coverImage}
              alt="learning path cover"
            />
            {learningPath.isCompleted && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'accent.main',
                  }}>
                  <CheckmarkIcon sx={{ width: 32, height: 32 }} />
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minWidth: 0,
              [containerQueries.up(LARGE_BREAKPOINT)]: {
                justifyContent: 'space-around',
              },
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                my: 'auto',
                [containerQueries.up(LARGE_BREAKPOINT)]: {
                  gap: 1.5,
                },
              }}>
              <Box
                sx={theme => ({
                  minHeight: `calc(${theme.typography.h4.lineHeight} * 2)`,
                  display: 'flex',
                  alignItems: 'flex-end',
                  [containerQueries.up(LARGE_BREAKPOINT)]: {
                    minHeight: 0,
                    alignItems: 'stretch',
                  },
                })}>
                <Typography
                  variant="h4"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                  {learningPath.title}
                </Typography>
              </Box>
              <Typography
                sx={theme => ({
                  display: 'none',
                  [containerQueries.up(LARGE_BREAKPOINT)]: {
                    display: '-webkit-box',
                  },
                  ...theme.typography.body3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  color: 'text.main',
                  my: 0.5,
                  '& p': {
                    margin: 0,
                  },
                })}
                dangerouslySetInnerHTML={{ __html: learningPath.description }}
              />
              <Typography variant="caption1Bold" sx={{ color: 'text.main' }}>
                {learningPath.topic?.name}
              </Typography>
            </Box>
            <Box>
              <Box
                sx={{
                  width: 200,
                  maxWidth: '100%',
                  [containerQueries.up(MEDIUM_BREAKPOINT)]: { width: 260 },
                }}>
                <ProgressBar
                  progressFraction={progressFraction}
                  skinny
                  progressColor={
                    learningPath.isCompleted ? 'accent.main' : 'secondary.main'
                  }
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 1,
                }}>
                <Box
                  sx={{
                    display: 'none',
                    [containerQueries.up(MEDIUM_BREAKPOINT)]: {
                      display: 'block',
                    },
                  }}>
                  <CreditsDurationInfo
                    credits={learningPath.credits}
                    duration={learningPath.duration}
                    durationPlayed={learningPath.durationPlayed}
                  />
                </Box>

                <EpisodeCountWithCompletions
                  episodeCount={episodeCount}
                  completions={learningPath.completions}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </Box>
  );
};
